import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, Navigation, MapPin, TrendingUp, 
  Loader2, AlertCircle, Compass, Target
} from 'lucide-react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Polyline, Marker, Circle, useMap } from 'react-leaflet';
import DynamicTileLayer from '@/components/DynamicTileLayer';
import MapSelector from '@/components/MapSelector';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Custom marker icons
const currentLocationIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const startIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const endIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Map component that follows user
function FollowUserOnMap({ position, trackPositions, followMode }) {
  const map = useMap();
  
  useEffect(() => {
    if (position && followMode) {
      map.setView(position, map.getZoom());
    }
  }, [position, followMode, map]);
  
  // Fit to track on first load
  useEffect(() => {
    if (trackPositions && trackPositions.length > 0 && !position) {
      map.fitBounds(trackPositions, { padding: [50, 50] });
    }
  }, [trackPositions, position, map]);
  
  return null;
}

// Haversine distance
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000; // Earth radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Returns meters
}

// Find closest point on track
function findClosestPointOnTrack(position, trackPoints) {
  let minDist = Infinity;
  let closestIdx = 0;
  
  for (let i = 0; i < trackPoints.length; i++) {
    const dist = calculateDistance(
      position[0], position[1],
      trackPoints[i][0], trackPoints[i][1]
    );
    if (dist < minDist) {
      minDist = dist;
      closestIdx = i;
    }
  }
  
  return { index: closestIdx, distance: minDist };
}

export default function FollowTrack() {
  const { trackId } = useParams();
  const navigate = useNavigate();
  
  const [track, setTrack] = useState(null);
  const [trackPoints, setTrackPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [currentPosition, setCurrentPosition] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [followMode, setFollowMode] = useState(true);
  
  const [distanceToTrack, setDistanceToTrack] = useState(null);
  const [distanceRemaining, setDistanceRemaining] = useState(null);
  const [closestPointIndex, setClosestPointIndex] = useState(0);
  
  const watchIdRef = useRef(null);

  // Fetch track data
  useEffect(() => {
    const fetchTrack = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/user/tracks/${trackId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setTrack(response.data);
        
        // Extract track points
        if (response.data.track_points && response.data.track_points.length > 0) {
          const points = response.data.track_points.map(p => [p.lat, p.lon]);
          setTrackPoints(points);
        }
      } catch (err) {
        console.error('Error fetching track:', err);
        setError('No se pudo cargar el track');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTrack();
  }, [trackId]);

  // Start GPS tracking
  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setGpsError('GPS no disponible');
      return;
    }
    
    setIsTracking(true);
    
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newPos = [latitude, longitude];
        setCurrentPosition(newPos);
        setGpsError(null);
        
        // Calculate distance to track
        if (trackPoints.length > 0) {
          const { index, distance } = findClosestPointOnTrack(newPos, trackPoints);
          setDistanceToTrack(distance);
          setClosestPointIndex(index);
          
          // Calculate remaining distance along track
          let remaining = 0;
          for (let i = index; i < trackPoints.length - 1; i++) {
            remaining += calculateDistance(
              trackPoints[i][0], trackPoints[i][1],
              trackPoints[i+1][0], trackPoints[i+1][1]
            );
          }
          setDistanceRemaining(remaining);
        }
      },
      (err) => {
        console.error('GPS error:', err);
        setGpsError('Error de GPS');
      },
      {
        enableHighAccuracy: true,
        maximumAge: 3000,
        timeout: 10000
      }
    );
    
    toast.success('Navegación iniciada');
  }, [trackPoints]);

  // Stop GPS tracking
  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setIsTracking(false);
    toast.info('Navegación pausada');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  // Format distance
  const formatDistance = (meters) => {
    if (meters === null || meters === undefined) return '-';
    if (meters < 1000) return `${Math.round(meters)} m`;
    return `${(meters / 1000).toFixed(1)} km`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-orange-500 mx-auto" />
          <p className="text-gray-600 mt-3">Cargando track...</p>
        </div>
      </div>
    );
  }

  if (error || !track) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="p-6 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-800">{error || 'Track no encontrado'}</h2>
          <p className="text-gray-600 mt-2">No se pudo cargar el track solicitado.</p>
          <Button onClick={() => navigate('/profile')} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al perfil
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm p-3 flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/profile')}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Volver
        </Button>
        <div className="text-center flex-1">
          <h1 className="font-semibold text-gray-800 truncate">{track.name}</h1>
          {track.distance_km && (
            <p className="text-xs text-gray-500">{track.distance_km.toFixed(1)} km</p>
          )}
        </div>
        <Badge variant={isTracking ? "default" : "secondary"}>
          {isTracking ? "Navegando" : "Pausado"}
        </Badge>
      </div>
      
      {/* Map */}
      <div className="flex-1 relative">
        {/* Map Style Selector */}
        <div className="absolute top-2 right-2 z-[1000]">
          <MapSelector compact />
        </div>
        
        {trackPoints.length > 0 ? (
          <MapContainer
            center={trackPoints[0]}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            className="z-0"
          >
            <DynamicTileLayer />
            
            {/* Track line */}
            <Polyline
              positions={trackPoints}
              color="#f97316"
              weight={5}
              opacity={0.8}
            />
            
            {/* Start marker */}
            <Marker position={trackPoints[0]} icon={startIcon} />
            
            {/* End marker */}
            <Marker position={trackPoints[trackPoints.length - 1]} icon={endIcon} />
            
            {/* Current position */}
            {currentPosition && (
              <>
                <Marker position={currentPosition} icon={currentLocationIcon} />
                <Circle
                  center={currentPosition}
                  radius={50}
                  pathOptions={{ color: '#ef4444', fillColor: '#ef4444', fillOpacity: 0.2 }}
                />
              </>
            )}
            
            {/* Closest point on track indicator */}
            {currentPosition && trackPoints[closestPointIndex] && (
              <Circle
                center={trackPoints[closestPointIndex]}
                radius={20}
                pathOptions={{ color: '#22c55e', fillColor: '#22c55e', fillOpacity: 0.5 }}
              />
            )}
            
            <FollowUserOnMap 
              position={currentPosition} 
              trackPositions={trackPoints}
              followMode={followMode}
            />
          </MapContainer>
        ) : (
          <div className="h-full flex items-center justify-center bg-gray-200">
            <p className="text-gray-500">No hay puntos de track disponibles</p>
          </div>
        )}
        
        {/* GPS Error overlay */}
        {gpsError && (
          <div className="absolute top-4 left-4 right-4 bg-red-100 border border-red-300 rounded-lg p-3 z-[1000]">
            <p className="text-red-700 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {gpsError}
            </p>
          </div>
        )}
      </div>
      
      {/* Bottom panel */}
      <div className="bg-white shadow-lg p-4 space-y-3">
        {/* Stats */}
        {isTracking && (
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <Target className="w-5 h-5 mx-auto text-orange-500 mb-1" />
              <p className="text-xs text-gray-500">Distancia al track</p>
              <p className="font-bold text-lg">
                {distanceToTrack !== null && distanceToTrack > 100 ? (
                  <span className="text-red-500">{formatDistance(distanceToTrack)}</span>
                ) : (
                  <span className="text-green-500">{formatDistance(distanceToTrack)}</span>
                )}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <TrendingUp className="w-5 h-5 mx-auto text-orange-500 mb-1" />
              <p className="text-xs text-gray-500">Restante</p>
              <p className="font-bold text-lg">{formatDistance(distanceRemaining)}</p>
            </div>
          </div>
        )}
        
        {/* Controls */}
        <div className="flex gap-2">
          {!isTracking ? (
            <Button
              onClick={startTracking}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <Navigation className="w-4 h-4 mr-2" />
              Iniciar Navegación
            </Button>
          ) : (
            <>
              <Button
                onClick={stopTracking}
                variant="outline"
                className="flex-1"
              >
                Pausar
              </Button>
              <Button
                onClick={() => setFollowMode(!followMode)}
                variant={followMode ? "default" : "outline"}
                className={followMode ? "bg-blue-600 hover:bg-blue-700" : ""}
              >
                <Compass className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
        
        {!isTracking && (
          <p className="text-xs text-gray-500 text-center">
            Inicia la navegación para ver tu posición en el mapa
          </p>
        )}
      </div>
    </div>
  );
}
